import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { STAGE_LABELS, STAGES, SOURCE_LABELS, TARGET_LABELS } from "../lib/constants";
import { LevelBadge } from "../components/LevelBadge";
import NewLeadDialog from "../components/NewLeadDialog";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Search, Loader2, Skull, Undo2, RotateCcw, ParkingCircle, Users, Handshake, ShieldAlert, Map as MapIcon, MapPin, Copy, Combine, Globe, Radio } from "lucide-react";
import { toast } from "sonner";
import EntityMap from "../components/EntityMap";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

const STAGE_COLORS = { lead: "#9CA3AF", profilazione: "#60A5FA", consulenza: "#A78BFA", preventivo: "#F59E0B", contratto: "#34D399" };

export default function Leads() {
  const [leads, setLeads] = useState(null);
  const [cemetery, setCemetery] = useState(null);
  const [parking, setParking] = useState(null);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ stage: "all", target: "all", level: "all", source: "all" });
  const [spamOnly, setSpamOnly] = useState(false);
  const [geo, setGeo] = useState(null);
  const [geoBusy, setGeoBusy] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["admin", "team_leader"].includes(user?.role);
  const [dedup, setDedup] = useState(null);
  const [dedupBusy, setDedupBusy] = useState(false);

  const openDedup = async () => {
    try { const { data } = await api.get("/leads/dedup/preview"); setDedup(data); }
    catch { toast.error("Impossibile calcolare i duplicati"); }
  };
  const applyDedup = async () => {
    setDedupBusy(true);
    try {
      const { data } = await api.post("/leads/dedup/apply");
      toast.success(`Unione completata: ${data.deleted} eliminati, ${data.linked} collegati`);
      setDedup(null); load(); loadGeo();
    } catch { toast.error("Errore durante l'unione"); }
    finally { setDedupBusy(false); }
  };

  const loadGeo = useCallback(() => {
    api.get("/leads-geo").then((r) => setGeo(r.data)).catch(() => setGeo({ points: [], pending: 0 }));
  }, []);
  useEffect(() => { loadGeo(); }, [loadGeo]);

  const runLeadGeocoding = async () => {
    setGeoBusy(true);
    try {
      let remaining = 1;
      while (remaining > 0) {
        const { data } = await api.post("/leads-geo/geocode-pending?limit=20");
        remaining = data.remaining;
        if (data.processed === 0) break;
      }
      toast.success("Geolocalizzazione lead completata");
      loadGeo();
    } catch {
      toast.error("Geolocalizzazione interrotta");
    } finally { setGeoBusy(false); }
  };

  const load = useCallback(() => {
    const params = { q: q || undefined, enrich: true };
    Object.entries(filters).forEach(([k, v]) => { if (v !== "all") params[k] = v; });
    if (spamOnly) params.spam = true;
    api.get("/leads", { params }).then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, [q, filters, spamOnly]);

  const loadCemetery = useCallback(() => {
    api.get("/leads", { params: { cemetery: true, enrich: true } }).then((r) => setCemetery(r.data)).catch(() => setCemetery([]));
  }, []);

  const loadParking = useCallback(() => {
    api.get("/leads", { params: { parking: true, enrich: true } }).then((r) => setParking(r.data)).catch(() => setParking([]));
  }, []);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { loadCemetery(); loadParking(); }, [loadCemetery, loadParking]);

  const restore = async (e, id) => {
    e.stopPropagation();
    try {
      await api.post(`/leads/${id}/cemetery/restore`);
      toast.success("Lead ripristinato");
      load(); loadCemetery();
    } catch {
      toast.error("Errore nel ripristino");
    }
  };

  const reactivateParking = async (e, id) => {
    e.stopPropagation();
    try {
      await api.post(`/automations/leads/${id}/reactivate`);
      toast.success("Lead riattivato dal Parcheggio");
      load(); loadParking();
    } catch {
      toast.error("Errore nella riattivazione");
    }
  };

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Lead</h1>
          <p className="text-xs text-muted-foreground">Elenco completo e filtri</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && <Button variant="outline" size="sm" onClick={openDedup} data-testid="dedup-open-btn"><Combine className="h-4 w-4 mr-1.5" /> Unisci duplicati</Button>}
          <NewLeadDialog onCreated={load} />
        </div>
      </header>

      <Dialog open={!!dedup} onOpenChange={(v) => { if (!v) setDedup(null); }}>
        <DialogContent className="bg-popover sm:max-w-lg" data-testid="dedup-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Unisci lead duplicati</DialogTitle>
            <DialogDescription className="text-xs">Stesso cliente + stesso indirizzo → tiene il lead più avanzato/recente (gli altri eliminati, trasferendo timeline, allegati, bolletta e tetto). Indirizzi diversi → restano come opzioni collegate.</DialogDescription>
          </DialogHeader>
          {dedup && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded border border-border p-2" data-testid="dedup-groups"><div className="text-xl font-bold">{dedup.groups}</div><div className="text-[10px] uppercase text-muted-foreground">Clienti</div></div>
                <div className="rounded border border-border p-2" data-testid="dedup-to-delete"><div className="text-xl font-bold text-amber-600">{dedup.to_delete}</div><div className="text-[10px] uppercase text-muted-foreground">Da eliminare</div></div>
                <div className="rounded border border-border p-2" data-testid="dedup-to-link"><div className="text-xl font-bold">{dedup.to_link}</div><div className="text-[10px] uppercase text-muted-foreground">Da collegare</div></div>
              </div>
              {dedup.to_delete === 0 && dedup.to_link === 0
                ? <p className="text-sm text-muted-foreground">Nessun duplicato da unire. 🎉</p>
                : <p className="text-xs text-amber-600">⚠️ Operazione irreversibile: verranno eliminati {dedup.to_delete} lead duplicati (i dati vengono trasferiti sul lead mantenuto).</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDedup(null)} data-testid="dedup-cancel-btn">Annulla</Button>
            <Button onClick={applyDedup} disabled={dedupBusy || !dedup || (dedup.to_delete === 0 && dedup.to_link === 0)} data-testid="dedup-apply-btn">{dedupBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Unisci ora</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="p-4 lg:p-8 space-y-4">
        <Card className="rounded border-border p-4" data-testid="leads-map-card">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><MapIcon className="h-4 w-4 text-primary" strokeWidth={1.5} /> Mappa lead geolocalizzati ({geo?.points?.length ?? 0})</h3>
            <div className="flex items-center gap-2">
              {geo?.pending > 0 && (
                <Button variant="outline" size="sm" onClick={runLeadGeocoding} disabled={geoBusy} data-testid="leads-geocode-btn">
                  {geoBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <MapPin className="h-3.5 w-3.5 mr-1.5" />} Geolocalizza ({geo.pending})
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setShowMap((s) => !s)} data-testid="leads-map-toggle">{showMap ? "Nascondi mappa" : "Mostra mappa"}</Button>
            </div>
          </div>
          {showMap && (
            (geo?.points?.length ?? 0) > 0
              ? <EntityMap points={geo.points.map((p) => ({ id: p.id, lat: p.lat, lon: p.lon, color: STAGE_COLORS[p.stage] || "#FDD003", title: `${p.name}${p.city ? " — " + p.city : ""} (${STAGE_LABELS[p.stage] || p.stage})` }))} height={400} onMarkerClick={(id) => navigate(`/leads/${id}`)} />
              : <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">Nessun lead geolocalizzato. Usa "Geolocalizza" per posizionare i lead sulla mappa.</div>
          )}
        </Card>

        <Tabs defaultValue="active" className="w-full">
          <TabsList data-testid="leads-tabs">
            <TabsTrigger value="active" data-testid="leads-tab-active" className="gap-1.5">
              <Users className="h-3.5 w-3.5" /> Lead attivi
              <span className="ml-1 text-[10px] bg-secondary rounded px-1.5 py-0.5">{leads?.length ?? "…"}</span>
            </TabsTrigger>
            <TabsTrigger value="parking" data-testid="leads-tab-parking" className="gap-1.5">
              <ParkingCircle className="h-3.5 w-3.5" /> Parcheggio
              <span className="ml-1 text-[10px] bg-secondary rounded px-1.5 py-0.5">{parking?.length ?? "…"}</span>
            </TabsTrigger>
            <TabsTrigger value="cemetery" data-testid="leads-tab-cemetery" className="gap-1.5">
              <Skull className="h-3.5 w-3.5" /> Cimitero
              <span className="ml-1 text-[10px] bg-secondary rounded px-1.5 py-0.5">{cemetery?.length ?? "…"}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active" className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome..." className="pl-9" data-testid="leads-search" />
              </div>
              <FilterSelect testid="filter-stage" value={filters.stage} onChange={(v) => setFilter("stage", v)} placeholder="Fase" options={STAGES.map((s) => [s, STAGE_LABELS[s]])} />
              <FilterSelect testid="filter-target" value={filters.target} onChange={(v) => setFilter("target", v)} placeholder="Target" options={Object.entries(TARGET_LABELS)} />
              <FilterSelect testid="filter-level" value={filters.level} onChange={(v) => setFilter("level", v)} placeholder="Livello" options={[["1", "🟢 Livello 1"], ["2", "🟡 Livello 2"], ["3", "🔴 Livello 3"]]} />
              <FilterSelect testid="filter-source" value={filters.source} onChange={(v) => setFilter("source", v)} placeholder="Origine" options={Object.entries(SOURCE_LABELS)} />
              <Button variant={spamOnly ? "default" : "outline"} onClick={() => setSpamOnly((s) => !s)} data-testid="filter-spam-toggle" className="gap-1.5">
                <ShieldAlert className="h-4 w-4" /> {spamOnly ? "Solo sospetti" : "Sospetti"}
              </Button>
            </div>

            <Card className="rounded border-border overflow-x-auto">
              {!leads ? (
                <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
              ) : leads.length === 0 ? (
                <p className="p-10 text-center text-sm text-muted-foreground">Nessun lead trovato.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-border">
                      <TableHead className="text-xs uppercase tracking-wider">Nome</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Città</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Partner più vicino</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Già processato</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Data inserimento</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Target</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Origine</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Fase</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Assegnato</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-right">Punteggio</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Livello consulenza</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-right">Impianto</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-right">Accumulo</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-right">Valore</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((l) => (
                      <TableRow key={l.id} onClick={() => navigate(`/leads/${l.id}`)} data-testid={`lead-row-${l.id}`} className="cursor-pointer border-border">
                        <TableCell className="font-medium"><span className="flex items-center gap-2">{l.name}{l.source === "partner_commerciale" && <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-600 text-[10px] font-semibold px-1.5 py-0.5" data-testid={`partner-badge-${l.id}`}><Handshake className="h-3 w-3" />Partner commerciale</span>}{l.spam_suspect && <span className="inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 text-red-600 text-[10px] font-semibold px-1.5 py-0.5" data-testid={`spam-badge-${l.id}`} title={(l.web_quality?.reasons || []).join(" · ")}><ShieldAlert className="h-3 w-3" />Sospetto{l.web_quality?.score != null ? ` ${l.web_quality.score}` : ""}</span>}{l.opportunities_count > 1 && <span className="inline-flex items-center gap-1 rounded-full border border-blue-500/40 bg-blue-500/10 text-blue-600 text-[10px] font-semibold px-1.5 py-0.5" data-testid={`opps-badge-${l.id}`} title="Questo cliente ha più opportunità / cantieri"><Copy className="h-3 w-3" />{l.opportunities_count} opportunità</span>}<OriginBadge lead={l} /></span></TableCell>
                        <TableCell className="text-muted-foreground text-sm max-w-[180px] truncate" data-testid={`lead-city-${l.id}`}>{l.city_display || "—"}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap" data-testid={`lead-partner-${l.id}`}>{l.nearest_partner ? `${l.nearest_partner.name} (${l.nearest_partner.km} km)` : "—"}</TableCell>
                        <TableCell data-testid={`lead-processed-${l.id}`}>{(l.processed && l.processed.length) ? (<div className="flex flex-wrap gap-1">{l.processed.map((p, i) => (<span key={i} className="inline-flex items-center rounded-full border border-border bg-secondary/50 text-[10px] px-1.5 py-0.5 whitespace-nowrap">{p.channel}{p.date ? ` · ${new Date(p.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}` : ""}</span>))}</div>) : (<span className="text-muted-foreground text-sm">—</span>)}</TableCell>
                        <TableCell className="text-muted-foreground text-sm whitespace-nowrap" data-testid={`lead-created-${l.id}`}>{l.created_at ? new Date(l.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{TARGET_LABELS[l.target]}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{SOURCE_LABELS[l.source]}</TableCell>
                        <TableCell className="text-sm">{STAGE_LABELS[l.stage]}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{l.assigned_to_name || "—"}</TableCell>
                        <TableCell className="text-right font-heading font-bold">{l.level ? `${l.score}` : "—"}</TableCell>
                        <TableCell><LevelBadge level={l.level} /></TableCell>
                        <TableCell className="text-right font-heading" data-testid={`lead-pv-${l.id}`}>{l.analysis?.pv_kwp ? `${l.analysis.pv_kwp} kWp` : "—"}</TableCell>
                        <TableCell className="text-right font-heading" data-testid={`lead-storage-${l.id}`}>{l.analysis?.storage_kwh ? `${l.analysis.storage_kwh} kWh` : "—"}</TableCell>
                        <TableCell className="text-right font-heading" data-testid={`lead-value-${l.id}`}>{l.cost_estimate?.prezzo ? Number(l.cost_estimate.prezzo).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }) : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="parking">
            <Card className="rounded border-border overflow-x-auto" data-testid="parking-section">
              {!parking ? (
                <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
              ) : parking.length === 0 ? (
                <p className="p-10 text-center text-sm text-muted-foreground">Nessun lead in parcheggio.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-border">
                      <TableHead className="text-xs uppercase tracking-wider">Nome</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Città</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Target</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Origine</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Fase</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Data inserimento</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parking.map((l) => (
                      <TableRow key={l.id} onClick={() => navigate(`/leads/${l.id}`)} data-testid={`parking-row-${l.id}`} className="cursor-pointer border-border">
                        <TableCell className="font-medium">{l.name}</TableCell>
                        <TableCell className="text-muted-foreground text-sm max-w-[180px] truncate">{l.city_display || "—"}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{TARGET_LABELS[l.target]}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{SOURCE_LABELS[l.source]}</TableCell>
                        <TableCell className="text-sm">{STAGE_LABELS[l.stage]}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{l.created_at ? new Date(l.created_at).toLocaleDateString("it-IT") : "—"}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={(e) => reactivateParking(e, l.id)} data-testid={`reactivate-${l.id}`} className="text-muted-foreground hover:text-foreground"><RotateCcw className="h-4 w-4 mr-1.5" /> Riattiva</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="cemetery">
            <Card className="rounded border-border overflow-x-auto" data-testid="cemetery-section">
              {!cemetery ? (
                <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
              ) : cemetery.length === 0 ? (
                <p className="p-10 text-center text-sm text-muted-foreground">Nessun lead nel cimitero.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-border">
                      <TableHead className="text-xs uppercase tracking-wider">Nome</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Target</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Origine</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Motivo</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Data</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cemetery.map((l) => (
                      <TableRow key={l.id} onClick={() => navigate(`/leads/${l.id}`)} data-testid={`cemetery-row-${l.id}`} className="cursor-pointer border-border">
                        <TableCell className="font-medium">{l.name}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{TARGET_LABELS[l.target]}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{SOURCE_LABELS[l.source]}</TableCell>
                        <TableCell className="text-muted-foreground text-sm max-w-[220px] truncate">{l.cemetery_reason || "—"}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{l.cemetery_at ? new Date(l.cemetery_at).toLocaleDateString("it-IT") : "—"}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={(e) => restore(e, l.id)} data-testid={`restore-${l.id}`} className="text-muted-foreground hover:text-foreground"><Undo2 className="h-4 w-4 mr-1.5" /> Ripristina</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testid }) {  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[160px]" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{placeholder}: Tutti</SelectItem>
        {options.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function OriginBadge({ lead }) {
  const isRete = lead.channel === "rete" || !!lead.rete_agent;
  if (isRete) {
    const ag = lead.rete_agent?.name;
    return <span className="inline-flex items-center gap-1 rounded-full border border-indigo-500/40 bg-indigo-500/10 text-indigo-600 text-[10px] font-semibold px-1.5 py-0.5" data-testid={`origin-badge-${lead.id}`} title="Configurazione avviata dalla rete (rete.torri.solar)"><Radio className="h-3 w-3" />Rete{ag ? ` · ${ag}` : ""}</span>;
  }
  if (lead.created_by_name === "Self-service (web)") {
    return <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 text-[10px] font-semibold px-1.5 py-0.5" data-testid={`origin-badge-${lead.id}`} title="Lead self-service (web pubblico)"><Globe className="h-3 w-3" />Self-service</span>;
  }
  if (lead.created_by_name === "Strumento Free (web)" || lead.is_free) {
    return <span className="inline-flex items-center gap-1 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-600 text-[10px] font-semibold px-1.5 py-0.5" data-testid={`origin-badge-${lead.id}`} title="Lead da strumento Free (web)"><Globe className="h-3 w-3" />Free web</span>;
  }
  return null;
}
